// Shared axios client for the agent PWA. Mirrors the admin portal's client: injects the
// agent bearer token, unwraps the { success, data, pagination } envelope, and on 401
// refreshes the token once (queueing concurrent requests) before retrying.
import axios from 'axios';
import { mockAdapter } from './mocks';

export const BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';
const USE_MOCKS = String(import.meta.env.VITE_USE_MOCKS) === 'true';

const KEYS = {
  access: 'cid_agent_access_token',
  refresh: 'cid_agent_refresh_token',
  agent: 'cid_agent_user',
};

// "View as agent": HQ opened this agent's CIDAS from the admin portal, read-only.
// The session lives in sessionStorage, so it belongs to this one tab: it never
// touches a real sign-in kept in localStorage on the same computer, and closing
// the tab ends it there. There is no refresh token; when the server says the
// session is over, the tab shows that instead of the login page.
const VIEW_KEY = 'cid_view_as';
// "This tab was opened for a view." Set on arrival and NOT dropped when the view
// ends: once the session is gone, Back or a typed "/" in the same tab would
// otherwise load this browser's own sign-in — a different, real agent, writable,
// with no banner — in a tab HQ still thinks of as the view. A new tab has its own
// sessionStorage, so the real sign-in there is untouched.
const VIEW_TAB_KEY = 'cid_view_as_tab';
const session = {
  get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* storage blocked */ } },
  remove(k) { try { sessionStorage.removeItem(k); } catch { /* storage blocked */ } },
};
export const viewAs = {
  get() {
    try { return JSON.parse(session.get(VIEW_KEY) || 'null'); } catch { return null; }
  },
  get active() { return !!this.get()?.accessToken; },
  // The view in this tab is over: only the ended page is shown, on any route.
  get ended() { return !this.active && session.get(VIEW_TAB_KEY) === '1'; },
  // The tab is (again) a view tab — called when a view link is opened in it.
  markTab() { session.set(VIEW_TAB_KEY, '1'); },
  set(v) { session.set(VIEW_TAB_KEY, '1'); session.set(VIEW_KEY, JSON.stringify(v)); },
  clear() { session.remove(VIEW_KEY); },
};
export const VIEW_ENDED_PATH = '/view-as/ended';
// The two view pages run before (or after) any session of their own exists, so
// nothing on them may call the API with whatever sign-in this browser holds.
export const onViewAsPath = () =>
  typeof window !== 'undefined' && ['/view-as', VIEW_ENDED_PATH].includes(window.location.pathname);
// Sends the tab to the ended page without leaving the view's screens behind it
// in history, so Back cannot step into them.
export const endViewInTab = () => {
  viewAs.clear();
  if (typeof window !== 'undefined') window.location.replace(VIEW_ENDED_PATH);
};

export const tokens = {
  get access() { return viewAs.active ? viewAs.get().accessToken : localStorage.getItem(KEYS.access); },
  get refresh() { return viewAs.active ? null : localStorage.getItem(KEYS.refresh); },
  get agent() {
    if (viewAs.active) return viewAs.get().agent || null;
    try { return JSON.parse(localStorage.getItem(KEYS.agent) || 'null'); } catch { return null; }
  },
  set({ accessToken, refreshToken, agent }) {
    if (viewAs.active) {
      // Only the viewed agent's own profile may update the view. Anything else is
      // this browser's real sign-in landing late — e.g. the profile fetch the app
      // starts on /view-as before the code is traded, or a refresh — and must not
      // become the view.
      const v = viewAs.get();
      if (agent && !accessToken && !refreshToken && agent.id === v.agent?.id) viewAs.set({ ...v, agent });
      return;
    }
    if (accessToken) localStorage.setItem(KEYS.access, accessToken);
    if (refreshToken) localStorage.setItem(KEYS.refresh, refreshToken);
    if (agent) localStorage.setItem(KEYS.agent, JSON.stringify(agent));
  },
  clear() {
    if (viewAs.active) { viewAs.clear(); return; }
    Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
  },
};

const client = axios.create({ baseURL: BASE, timeout: 20000 });
if (USE_MOCKS) client.defaults.adapter = mockAdapter(axios.getAdapter('xhr'));

client.interceptors.request.use((config) => {
  const t = tokens.access;
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ---- single-flight refresh shared across concurrent 401s ----
let refreshing = null;
function doRefresh() {
  if (!refreshing) {
    refreshing = axios
      .create({ baseURL: BASE, adapter: USE_MOCKS ? mockAdapter(axios.getAdapter('xhr')) : undefined })
      .post('/api/agent/auth/refresh', { refresh_token: tokens.refresh })
      .then((r) => {
        const env = r.data || {};
        const payload = env.data || env;
        tokens.set({ accessToken: payload.accessToken, refreshToken: payload.refreshToken, agent: payload.agent });
        return payload.accessToken;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

function normalizeError(error) {
  const env = error.response?.data;
  const e = new Error(env?.error?.message || env?.message || error.message || 'Request failed');
  e.code = env?.error?.code || error.code;
  e.status = error.response?.status;
  e.details = env?.error?.details;
  return e;
}

client.interceptors.response.use(
  (res) => res.data, // unwrap envelope → { success, message, data, pagination }
  async (error) => {
    const { response, config } = error;
    // A view session that ended (time up, ended from the portal) gets no refresh.
    if (response?.status === 401 && viewAs.active) {
      endViewInTab();
      return Promise.reject(normalizeError(error));
    }
    if (response?.status === 401 && config && !config._retry && tokens.refresh) {
      config._retry = true;
      try {
        const access = await doRefresh();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${access}`;
        // A request that presents the refresh token itself (sign-out after the access
        // token ran out) is retried with the one the renewal just issued: the renewal
        // revoked the one it was sent with, so the retry would revoke nothing and
        // leave the new one live on this device.
        if (typeof config.data === 'string' && config.data.includes('"refresh_token"')) {
          try { config.data = JSON.stringify({ ...JSON.parse(config.data), refresh_token: tokens.refresh }); } catch { /* not JSON */ }
        }
        return client(config);
      } catch (refreshError) {
        // Only the refresh token being *refused* means the session is over. A 429,
        // a 5xx or a dropped connection says nothing about whether it is still
        // valid — and signing the agent out on those is brutal in a busy session:
        // 100 agents who signed in together refresh together, and any that get
        // rate-limited would all land back on the login screen mid-task.
        // Anything else: keep the session, let the original error surface, and the
        // next request retries the refresh.
        const status = refreshError?.response?.status;
        if (status === 401 || status === 403) {
          tokens.clear();
          if (typeof window !== 'undefined' && !location.pathname.startsWith('/login')) {
            // Come back here after signing in — e.g. the event check-in link the
            // phone camera opened, query and all (the login pages read `next`).
            const next = `${location.pathname}${location.search}`;
            location.assign(next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`);
          }
        }
      }
    }
    return Promise.reject(normalizeError(error));
  }
);

export default client;
